import React, {
  ChangeEvent,
} from "react";

import {
  Category,
  ProductForm,
  FormErrors,
} from "./index";

interface ModalProps {
  title: string;

  submitLabel: string;

  setShowModal: () => void;

  form: ProductForm;

  handleChange: (
    e: ChangeEvent<
      | HTMLInputElement
      | HTMLTextAreaElement
      | HTMLSelectElement
    >
  ) => void;

  handleImageChange: (
    e: ChangeEvent<HTMLInputElement>
  ) => void;

  handleImagesChange: (
    e: ChangeEvent<HTMLInputElement>
  ) => void;

  handleSubmit: () => void;

  errors: FormErrors;

  preview: string | null;

  categories: Category[];

  subCategories: Category[];

  loadingCategories: boolean;

  loadingSubCategories: boolean;
}

export default function Modal({
  title,
  submitLabel,
  setShowModal,
  form,
  handleChange,
  handleImageChange,
  handleImagesChange,
  handleSubmit,
  errors,
  preview,
  categories,
  subCategories,
  loadingCategories,
  loadingSubCategories,
}: ModalProps) {
  return (
    <div className="modal">
      <div className="modal-box">

        {/* =====================================================
            HEADER
        ===================================================== */}

        <div className="modal-header">
          <h2>
            {title}
          </h2>

          <span
            className="close-btn"
            onClick={
              setShowModal
            }
          >
            ✕
          </span>
        </div>

        {/* =====================================================
            FORM
        ===================================================== */}

        <div className="form-grid">

          {/* ===================================================
              NAME
          =================================================== */}

          <div>
            <label>
              Product Name
            </label>

            <input
              type="text"
              name="name"
              placeholder="Product Name"
              value={
                form.name
              }
              onChange={
                handleChange
              }
            />

            {errors.name && (
              <div className="error">
                {errors.name}
              </div>
            )}
          </div>

          {/* ===================================================
              PRICE
          =================================================== */}

          <div>
            <label>
              Price
            </label>

            <input
              type="number"
              name="price"
              placeholder="Price"
              value={
                form.price
              }
              onChange={
                handleChange
              }
            />

            {errors.price && (
              <div className="error">
                {errors.price}
              </div>
            )}
          </div>

          {/* ===================================================
              MRP PRICE
          =================================================== */}

          <div>
            <label>
              MRP Price
            </label>

            <input
              type="number"
              name="mrp_price"
              placeholder="MRP Price"
              min="0"
              step="0.01"
              value={
                form.mrp_price ?? ""
              }
              onChange={
                handleChange
              }
            />

            {errors.mrp_price && (
              <div className="error">
                {
                  errors.mrp_price
                }
              </div>
            )}
          </div>

          {/* ===================================================
              BOMMENHALI PRICE
          =================================================== */}

          <div>
            <label>
              Bommenhali Price
            </label>

            <input
              type="number"
              name="bommenhali_price"
              placeholder="Bommenhali Price"
              value={
                form.bommenhali_price
              }
              onChange={
                handleChange
              }
            />

            {errors.bommenhali_price && (
              <div className="error">
                {
                  errors.bommenhali_price
                }
              </div>
            )}
          </div>

          {/* ===================================================
              KR PRICE
          =================================================== */}

          <div>
            <label>
              KR Price
            </label>

            <input
              type="number"
              name="kr_price"
              placeholder="KR Price"
              value={
                form.kr_price
              }
              onChange={
                handleChange
              }
            />

            {errors.kr_price && (
              <div className="error">
                {
                  errors.kr_price
                }
              </div>
            )}
          </div>

          {/* ===================================================
              QUANTITY
          =================================================== */}

          <div>
            <label>
              Quantity
            </label>

            <input
              type="number"
              name="quantity"
              placeholder="Quantity"
              value={
                form.quantity
              }
              onChange={
                handleChange
              }
            />

            {errors.quantity && (
              <div className="error">
                {
                  errors.quantity
                }
              </div>
            )}
          </div>

          {/* ===================================================
              CLOSING TIME
          =================================================== */}

          <div>
            <label>
              Order Closing Time
            </label>

            <input
              type="time"
              name="order_closing_time"
              value={
                form.order_closing_time
              }
              onChange={
                handleChange
              }
            />
          </div>

          {/* ===================================================
              STATUS - ACTIVE / INACTIVE
          =================================================== */}

          <div>
            <label>
              Status
            </label>

            <select
              name="status"
              className="category-filter"
              value={
                form.status ||
                "active"
              }
              onChange={
                handleChange
              }
            >
              <option value="active">
                Active
              </option>

              <option value="inactive">
                Inactive
              </option>
            </select>
          </div>

          {/* ===================================================
              CATEGORY
          =================================================== */}

          <div>
            <label>
              Category
            </label>

            <select
              name="category_id"
              className="category-filter"
              value={
                form.category_id
              }
              onChange={
                handleChange
              }
              disabled={
                loadingCategories
              }
            >
              <option value="">
                {loadingCategories
                  ? "Loading categories..."
                  : "Select Category"}
              </option>

              {categories.map(
                (category) => (
                  <option
                    key={
                      category._id
                    }
                    value={
                      category._id
                    }
                  >
                    {
                      category.name
                    }
                  </option>
                )
              )}
            </select>

            {errors.category_id && (
              <div className="error">
                {
                  errors.category_id
                }
              </div>
            )}
          </div>

          {/* ===================================================
              SUBCATEGORY
          =================================================== */}

          <div>
            <label>
              Subcategory
            </label>

            <select
              name="sub_category_id"
              className="category-filter"
              value={
                form.sub_category_id
              }
              onChange={
                handleChange
              }
              disabled={
                !form.category_id ||
                loadingSubCategories
              }
            >
              <option value="">
                {loadingSubCategories
                  ? "Loading subcategories..."
                  : !form.category_id
                  ? "Select Category First"
                  : "Select Subcategory"}
              </option>

              {subCategories.map(
                (
                  subCategory
                ) => (
                  <option
                    key={
                      subCategory._id
                    }
                    value={
                      subCategory._id
                    }
                  >
                    {
                      subCategory.name
                    }
                  </option>
                )
              )}
            </select>
          </div>

          {/* ===================================================
              DESCRIPTION
          =================================================== */}

          <div className="full">
            <label>
              Description
            </label>

            <textarea
              name="description"
              placeholder="Description"
              value={
                form.description
              }
              onChange={
                handleChange
              }
            />

            {errors.description && (
              <div className="error">
                {
                  errors.description
                }
              </div>
            )}
          </div>

          {/* ===================================================
              IMAGE
          =================================================== */}

          <div className="full">
            <label>
              Product Image
            </label>

            <input
              type="file"
              accept="image/*"
              onChange={
                handleImageChange
              }
            />
          </div>

          <div className="full">
            <label>
              Gallery Images
            </label>

            <input
              type="file"
              accept="image/*"
              multiple
              onChange={
                handleImagesChange
              }
            />

            <small>
              Select multiple product images. They will be sent using the
              &quot;images&quot; form-data key.
            </small>

            {form.images.length > 0 && (
              <div className="mt-2 text-sm">
                {form.images.length} gallery image
                {form.images.length > 1 ? "s" : ""} selected
              </div>
            )}
          </div>

          {/* ===================================================
              IMAGE PREVIEW
          =================================================== */}

          {preview && (
            <div className="full">
              <img
                src={
                  preview
                }
                alt="Preview"
                className="preview"
              />
            </div>
          )}

          {/* ===================================================
              SUBSCRIPTION
          =================================================== */}

          <div className="full checkbox-row">
            <input
              type="checkbox"
              name="isSubcibed"
              checked={
                form.isSubcibed
              }
              onChange={
                handleChange
              }
              style={{
                width: "auto",
                height: "auto",
              }}
            />

            <label>
              Subscription Product
            </label>
          </div>

          {/* ===================================================
              SUBSCRIPTION QUANTITY
          =================================================== */}

          {form.isSubcibed && (
            <div className="full">
              <label>
                Subscription Quantity
              </label>

              <input
                type="number"
                name="quantitySubcribed"
                placeholder="Subscription Quantity"
                value={
                  form.quantitySubcribed
                }
                onChange={
                  handleChange
                }
              />
            </div>
          )}

        </div>

        {/* =====================================================
            BUTTONS
        ===================================================== */}

        <div className="btn-group">
          <button
            type="button"
            className="btn secondary"
            onClick={
              setShowModal
            }
          >
            Cancel
          </button>

          <button
            type="button"
            className="btn primary"
            onClick={
              handleSubmit
            }
          >
            {submitLabel}
          </button>
        </div>

      </div>
    </div>
  );
}
